import { create } from 'zustand';
import type { BehaviourAgentExperienceAction } from '@/types/ui-blocks';

/**
 * Experiences a canvas slot can host. Behaviour-driven slots use the
 * agent-experience union; `report_canvas` is the host-driven report preview
 * (bound to the conversation via an INSIGHT execution resource), `briefing`
 * is the host-driven inbox-card briefing (bound via a CARD execution resource),
 * `routine_preview` is the host-driven routine draft preview mounted for
 * create/update-routine threads, `task_detail` is a task opened from the
 * cards the agent renders after creating them, and `artifact` is an
 * interactive page the agent authored, opened from its card.
 */
export type SlotExperience =
  | BehaviourAgentExperienceAction['experience']
  | 'report_canvas'
  | 'briefing'
  | 'routine_preview'
  | 'task_detail'
  | 'artifact';

export interface SlotRegistryEntry {
  id: string;
  experience: SlotExperience;
  title: string;
  chatHistoryId?: string;
  /**
   * Preferred default width (px) for this slot when the user hasn't dragged the
   * handle. The briefing panel is narrower than a full report canvas.
   */
  preferredWidthPx?: number;
  /**
   * Whether an in-stream preview chip (SlotCanvasPreview) exists for this slot
   * in the chat. The canvas frame shows its close (✕) button only when true —
   * a closed slot with a chip can be reopened by clicking it. Host-driven slots
   * with no chip (report canvas, briefing) leave this false, so the frame offers
   * collapse-only: closing would otherwise strand the slot with no way back.
   */
  hasInStreamCard?: boolean;
  registeredAt: number;
}

interface SlotCanvasState {
  openSlotId: string | null;
  /**
   * Slot to return to when the open slot is dismissed with "back" — e.g. an
   * artifact opened on top of the briefing returns to the briefing. Null when
   * the open slot is a top-level slot.
   */
  returnSlotId: string | null;
  /** Whole-panel collapsed (hidden, reopenable) vs. maximized (full-surface). */
  isCollapsed: boolean;
  isMaximized: boolean;
  registry: Record<string, SlotRegistryEntry>;
  /**
   * DOM element that slot bodies should portal into. Set by SlotCanvas
   * when its body mount div commits; cleared when the canvas unmounts.
   * Lives in the store (not a ref) so slot components re-render when it
   * becomes available.
   */
  bodyMountElement: HTMLElement | null;
  registerSlot: (entry: SlotRegistryEntry) => void;
  unregisterSlot: (id: string) => void;
  /**
   * Open a slot. Pass `{ returnTo }` to layer it over another slot (the layout
   * shows a back button); a plain open clears any return target. Opening always
   * un-collapses the panel.
   */
  openSlot: (id: string, opts?: { returnTo?: string }) => void;
  /** Return to `returnSlotId` (no-op when there is none). */
  goBack: () => void;
  closeSlot: () => void;
  setCollapsed: (collapsed: boolean) => void;
  setMaximized: (maximized: boolean) => void;
  setBodyMountElement: (node: HTMLElement | null) => void;
}

export const useSlotCanvasStore = create<SlotCanvasState>((set) => ({
  openSlotId: null,
  returnSlotId: null,
  isCollapsed: false,
  isMaximized: false,
  registry: {},
  bodyMountElement: null,
  registerSlot: (entry) =>
    set((state) => ({
      registry: { ...state.registry, [entry.id]: entry },
    })),
  unregisterSlot: (id) =>
    set((state) => {
      const closingOpen = state.openSlotId === id;
      // Bail out only when there is genuinely nothing to do. An id can be open
      // while absent from the registry (a slot that re-registered under a
      // changed identity, say), and that pairing is exactly what strands an
      // empty canvas in the next conversation — so unregistering must still
      // clear it.
      if (!(id in state.registry) && !closingOpen && state.returnSlotId !== id) {
        return state;
      }
      const nextRegistry = { ...state.registry };
      delete nextRegistry[id];
      return {
        registry: nextRegistry,
        openSlotId: closingOpen ? null : state.openSlotId,
        returnSlotId: state.returnSlotId === id ? null : state.returnSlotId,
        ...(closingOpen ? { isCollapsed: false, isMaximized: false } : {}),
      };
    }),
  openSlot: (id, opts) =>
    set({ openSlotId: id, returnSlotId: opts?.returnTo ?? null, isCollapsed: false }),
  goBack: () =>
    set((state) =>
      state.returnSlotId
        ? { openSlotId: state.returnSlotId, returnSlotId: null, isMaximized: false }
        : state,
    ),
  closeSlot: () =>
    set({ openSlotId: null, returnSlotId: null, isCollapsed: false, isMaximized: false }),
  setCollapsed: (collapsed) => set({ isCollapsed: collapsed }),
  setMaximized: (maximized) => set({ isMaximized: maximized }),
  setBodyMountElement: (node) => set({ bodyMountElement: node }),
}));
